import { Home } from "lucide-react";
import type { HouseholdListItem } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/DropdownMenu";
import { useHouseholdsList, useUpdateHousehold } from "@/shared/lib/households";

interface AddToHouseholdMenuProps {
  selectedIds: string[];
  onDone: () => void;
}

/**
 * Bulk action: pick a household, attach the selected contacts to it. The
 * spec routes membership changes through `PATCH /households/:id` with
 * `members.add` (see householdPatchSchema), so each pick instantiates its
 * own mutation hook.
 */
export function AddToHouseholdMenu({ selectedIds, onDone }: AddToHouseholdMenuProps) {
  const households = useHouseholdsList();
  const items = households.data?.items ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={selectedIds.length === 0}
          aria-label="Add to household"
        >
          <Home aria-hidden className="h-4 w-4" />
          <span className="hidden sm:inline">Add to household</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" sideOffset={20} className="min-w-[14rem]">
        <DropdownMenuLabel className="px-3 py-1.5 type-label-sm tracking-wider uppercase text-text-muted">
          Your households
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {households.isLoading ? (
          <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
        ) : items.length === 0 ? (
          <DropdownMenuItem disabled>
            No households yet — create one from Manage Households.
          </DropdownMenuItem>
        ) : (
          items.map((hh) => (
            <AddToHouseholdItem
              key={hh.id}
              household={hh}
              selectedIds={selectedIds}
              onDone={onDone}
            />
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface AddToHouseholdItemProps {
  household: HouseholdListItem;
  selectedIds: string[];
  onDone: () => void;
}

function householdName(hh: HouseholdListItem): string {
  return hh.displayNameCustom ?? "Unnamed household";
}

function AddToHouseholdItem({ household, selectedIds, onDone }: AddToHouseholdItemProps) {
  const update = useUpdateHousehold(household.id);
  function handleSelect() {
    update.mutate(
      { members: { add: selectedIds } },
      {
        onSuccess: () => onDone(),
      },
    );
  }
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        handleSelect();
      }}
      disabled={update.isPending}
    >
      <span className="flex-1 truncate">{householdName(household)}</span>
      <span className="type-body-sm text-text-muted">
        {update.isPending ? "Adding…" : household.memberCount}
      </span>
    </DropdownMenuItem>
  );
}
